import type {
  PaymentPlanDto,
  ApprovePaymentPlanItemDto,
  RequestChangesDto,
  CreateFundingIntentDto,
  FundingIntentDto,
  ConfirmDirectPaymentDto,
  PaymentConfirmationDto,
  DeclareOffPlatformPaymentDto,
  OffPlatformPaymentDto,
  RejectOffPlatformPaymentDto,
  InvoiceDetailDto,
  InvoiceDto,
  RaiseDisputeDto,
  DisputeDto,
  ResolveDisputeDto,
  CreateRefundRequestDto,
  RefundRequestDto,
  RejectRefundRequestDto,
} from '@paladin-monorepo/common';
import API from '../api';

export const paymentsApi = {
  async getByProjectId(projectId: string): Promise<PaymentPlanDto[]> {
    return API.get<PaymentPlanDto[]>(`/projects/${encodeURIComponent(projectId)}/payment-plans`).then(r => r.data);
  },
  /** POST …/payment-plans/:id/funding-intents - bez `paymentPlanItemIds` vybere server další escrow řádek. */
  async createFundingIntent(paymentPlanId: string, dto: CreateFundingIntentDto): Promise<FundingIntentDto> {
    return API.post<FundingIntentDto>(
      `/payment-plans/${encodeURIComponent(paymentPlanId)}/funding-intents`,
      dto,
    ).then(r => r.data);
  },
  async approveItem(itemId: string, dto?: ApprovePaymentPlanItemDto): Promise<void> {
    await API.post(`/payment-plan-items/${encodeURIComponent(itemId)}/approvals`, dto ?? {});
  },
  async requestChanges(itemId: string, dto: RequestChangesDto): Promise<void> {
    await API.post(`/payment-plan-items/${encodeURIComponent(itemId)}/change-requests`, dto);
  },

  async reportDirectPayment(
    itemId: string,
    dto: ConfirmDirectPaymentDto,
  ): Promise<PaymentConfirmationDto> {
    const { data } = await API.post<PaymentConfirmationDto>(
      `/payment-plan-items/${encodeURIComponent(itemId)}/payment-confirmations`,
      dto,
    );
    return data;
  },

  async confirmDirectPaymentReceipt(confirmationId: string): Promise<PaymentConfirmationDto> {
    const { data } = await API.post<PaymentConfirmationDto>(
      `/payment-confirmations/${encodeURIComponent(confirmationId)}/confirm-received`,
      {},
    );
    return data;
  },

  async declareOffPlatformPayment(
    itemId: string,
    dto: DeclareOffPlatformPaymentDto,
  ): Promise<OffPlatformPaymentDto> {
    const { data } = await API.post<OffPlatformPaymentDto>(
      `/payment-plan-items/${encodeURIComponent(itemId)}/off-platform-payments`,
      dto,
    );
    return data;
  },

  async confirmOffPlatformPayment(id: string): Promise<OffPlatformPaymentDto> {
    const { data } = await API.post<OffPlatformPaymentDto>(
      `/off-platform-payments/${encodeURIComponent(id)}/confirm`,
      {},
    );
    return data;
  },

  async rejectOffPlatformPayment(id: string, dto: RejectOffPlatformPaymentDto): Promise<OffPlatformPaymentDto> {
    const { data } = await API.post<OffPlatformPaymentDto>(
      `/off-platform-payments/${encodeURIComponent(id)}/reject`,
      dto,
    );
    return data;
  },

  async getTmInvoiceDetailForPlanItem(projectId: string, paymentPlanItemId: string): Promise<InvoiceDetailDto | null> {
    const { data } = await API.get<InvoiceDetailDto | null>(
      `/projects/${encodeURIComponent(projectId)}/timesheets/plan-items/${encodeURIComponent(paymentPlanItemId)}/invoice-detail`,
    );
    return data ?? null;
  },

  async approveInvoiceForPayment(invoiceId: string): Promise<InvoiceDto> {
    const { data } = await API.post<InvoiceDto>(
      `/invoices/${encodeURIComponent(invoiceId)}/approve-for-payment`,
      {},
    );
    return data;
  },

  async raiseDispute(paymentPlanItemId: string, dto: RaiseDisputeDto): Promise<DisputeDto> {
    const { data } = await API.post<DisputeDto>(
      `/payment-plan-items/${encodeURIComponent(paymentPlanItemId)}/disputes`,
      dto,
    );
    return data;
  },

  async listDisputes(status?: string): Promise<DisputeDto[]> {
    const { data } = await API.get<DisputeDto[]>('/disputes', {
      params: status ? { status } : undefined,
    });
    return data;
  },

  async getDispute(id: string): Promise<DisputeDto> {
    const { data } = await API.get<DisputeDto>(`/disputes/${encodeURIComponent(id)}`);
    return data;
  },

  async resolveDispute(id: string, dto: ResolveDisputeDto): Promise<DisputeDto> {
    const { data } = await API.post<DisputeDto>(`/disputes/${encodeURIComponent(id)}/resolutions`, dto);
    return data;
  },

  async listRefundRequestsByProject(projectId: string): Promise<RefundRequestDto[]> {
    const { data } = await API.get<RefundRequestDto[]>(
      `/projects/${encodeURIComponent(projectId)}/refund-requests`,
    );
    return data;
  },

  async createRefundRequest(projectId: string, dto: CreateRefundRequestDto): Promise<RefundRequestDto> {
    const { data } = await API.post<RefundRequestDto>(
      `/projects/${encodeURIComponent(projectId)}/refund-requests`,
      dto,
    );
    return data;
  },

  async listRefundRequestsOps(status?: string): Promise<RefundRequestDto[]> {
    const { data } = await API.get<RefundRequestDto[]>('/refund-requests', {
      params: status ? { status } : undefined,
    });
    return data;
  },

  async approveRefundRequest(id: string): Promise<RefundRequestDto> {
    const { data } = await API.post<RefundRequestDto>(`/refund-requests/${encodeURIComponent(id)}/approve`, {});
    return data;
  },

  async rejectRefundRequest(id: string, dto: RejectRefundRequestDto): Promise<RefundRequestDto> {
    const { data } = await API.post<RefundRequestDto>(`/refund-requests/${encodeURIComponent(id)}/reject`, dto);
    return data;
  },
};
